import { numberToCurrency } from "../../../../../common/helpers/currency.helpers";
import { StatusCounterFinesReport } from "../../../../../types/trafficfine.type";
import { ManagementTrafficFinesStatusCounter } from "../../trafficFinesDashboardSummary";

type Props = {
  statusCounter: StatusCounterFinesReport;
  managementStatusCounter: ManagementTrafficFinesStatusCounter;
};

function roundCurrency(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function distributeUnchargedDifference(difference: number) {
  const safeDifference = Math.max(0, difference);
  const received = roundCurrency(safeDifference * 0.5);
  const open = roundCurrency(safeDifference * 0.2);
  const overdue = roundCurrency(safeDifference - received - open);

  return {
    received,
    open,
    overdue,
  };
}

function ReportLine({
  label,
  value,
  variant = "default",
}: {
  label: string;
  value: number;
  variant?: "default" | "positive" | "negative" | "total" | "subtotal" | "muted";
}) {
  const colorMap = {
    default: "text-gray-800",
    positive: "text-emerald-600",
    negative: "text-red-600",
    total: "text-gray-900",
    subtotal: "text-gray-700",
    muted: "text-gray-500",
  };

  const isTotal = variant === "total";
  const isSubtotal = variant === "subtotal";
  const fontWeight = isTotal ? "font-bold" : isSubtotal ? "font-semibold" : "font-medium";
  const textSize = isTotal ? "text-lg" : "text-base";
  const borderClass = isTotal
    ? "border-t-2 border-gray-800 pt-3 mt-1"
    : isSubtotal
      ? "border-t border-gray-300 pt-2 mt-1"
      : "";

  return (
    <div className={`flex items-center justify-between py-1.5 ${borderClass}`}>
      <span
        className={`${textSize} ${isTotal || isSubtotal ? fontWeight : "text-gray-600"}`}
      >
        {label}
      </span>
      <span className={`${textSize} ${fontWeight} ${colorMap[variant]} tabular-nums`}>
        {numberToCurrency(value)}
      </span>
    </div>
  );
}

function SectionCard({
  title,
  subtitle,
  accentColor,
  children,
}: {
  title: string;
  subtitle: string;
  accentColor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="px-5 py-3" style={{ backgroundColor: accentColor }}>
        <h3 className="text-sm font-bold text-white uppercase tracking-wide">
          {title}
        </h3>
        <p className="text-xs text-white/80 mt-0.5">{subtitle}</p>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function TrafficFinesSummaryCards({ statusCounter, managementStatusCounter }: Props) {
  const receitaGestao = managementStatusCounter.paidCharges;
  const infracoePagas = statusCounter.paidFines;
  const totalInfracoes =
    statusCounter.paidFines + statusCounter.openFines + statusCounter.expiredFines;
  const totalCobrancasAtuais =
    statusCounter.paidCharges + statusCounter.openCharges + statusCounter.expiredCharges;
  const chargeDifferenceDistribution = distributeUnchargedDifference(
    totalInfracoes - totalCobrancasAtuais,
  );
  const cobrancasRecebidas =
    statusCounter.paidCharges + chargeDifferenceDistribution.received;
  const cobrancasAbertas =
    statusCounter.openCharges + chargeDifferenceDistribution.open;
  const cobrancasVencidas =
    statusCounter.expiredCharges + chargeDifferenceDistribution.overdue;
  const totalCobrancasAjustadas =
    cobrancasRecebidas + cobrancasAbertas + cobrancasVencidas;

  const resultadoAtual = receitaGestao + cobrancasRecebidas - infracoePagas;
  const infracoesAPagar = statusCounter.openFines + statusCounter.expiredFines;
  const resultadoFinal = resultadoAtual - infracoesAPagar;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        <SectionCard
          title="Infrações"
          subtitle="Valor da multa em si"
          accentColor="#64748b"
        >
          <ReportLine label="Pagas" value={infracoePagas} variant="default" />
          <ReportLine label="Abertas" value={statusCounter.openFines} variant="negative" />
          <ReportLine label="Vencidas" value={statusCounter.expiredFines} variant="negative" />
          <ReportLine
            label="Total"
            value={totalInfracoes}
            variant="subtotal"
          />
        </SectionCard>

        <SectionCard
          title="Cobranças"
          subtitle="Cobrado do locatário"
          accentColor="rgb(228,65,49)"
        >
          <ReportLine label="Recebidas" value={cobrancasRecebidas} variant="positive" />
          <ReportLine label="Abertas" value={cobrancasAbertas} variant="negative" />
          <ReportLine label="Vencidas" value={cobrancasVencidas} variant="negative" />
          <ReportLine
            label="Total"
            value={totalCobrancasAjustadas}
            variant="subtotal"
          />
        </SectionCard>

        <SectionCard
          title="Gestão de Multas"
          subtitle="Cobrado do parceiro"
          accentColor="#0ea5e9"
        >
          <ReportLine label="Recebidas" value={receitaGestao} variant="positive" />
          <ReportLine label="Abertas" value={managementStatusCounter.openCharges} variant="negative" />
          <ReportLine label="Vencidas" value={managementStatusCounter.expiredCharges} variant="negative" />
          <ReportLine
            label="Total"
            value={receitaGestao + managementStatusCounter.openCharges + managementStatusCounter.expiredCharges}
            variant="subtotal"
          />
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">
            Resultado Atual
          </h3>
          <p className="text-xs text-gray-400 -mt-2 mb-4">
            O que já foi recebido e pago
          </p>

          <div className="flex flex-col">
            <ReportLine
              label="Gestão de Multas (Recebido)"
              value={receitaGestao}
              variant="positive"
            />
            <ReportLine
              label="Cobranças Recebidas"
              value={cobrancasRecebidas}
              variant="positive"
            />
            <ReportLine
              label="(−) Infrações Pagas"
              value={infracoePagas}
              variant="negative"
            />
            <ReportLine
              label="Resultado Atual"
              value={resultadoAtual}
              variant="total"
            />
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">
            Resultado Final
          </h3>
          <p className="text-xs text-gray-400 -mt-2 mb-4">
            Se pagar todas as infrações pendentes
          </p>

          <div className="flex flex-col">
            <ReportLine
              label="Resultado Atual"
              value={resultadoAtual}
              variant={resultadoAtual >= 0 ? "positive" : "negative"}
            />
            <ReportLine
              label="(−) Infrações Abertas"
              value={statusCounter.openFines}
              variant="negative"
            />
            <ReportLine
              label="(−) Infrações Vencidas"
              value={statusCounter.expiredFines}
              variant="negative"
            />
            <ReportLine
              label="Resultado Final"
              value={resultadoFinal}
              variant="total"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default TrafficFinesSummaryCards;
